import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../api/api';
import { cleanTemplateInDatabase, analyzeTemplate } from '../../utils/templateCleanup';
import styles from './TemplateCreationWizard.module.css';

const TemplateCreationWizard = ({ template, isEditing = false, onTemplateCreated, onCancel }) => {
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  // Default template structure
  const defaultTemplate = {
    name: '',
    description: '',
    category: 'youth',
    visibility: 'club',
    selectedFields: {
      personalInfo: true,
      emergencyContact: true,
      healthInfo: false,
      parentGuardian: false,
      soccerExperience: false,
      medicalClearance: false
    },
    customFields: {
      personalInfo: [],
      emergencyContact: [],
      healthInfo: [],
      parentGuardian: [],
      soccerExperience: [],
      medicalClearance: []
    },
    selectedDocuments: {
      birthCertificate: false,
      medicalClearance: false,
      photoId: false,
      insuranceInfo: false
    },
    standardDocumentConfigs: {},
    customDocuments: []
  };

  // Convert API template format to component format
  const convertApiTemplateToComponentFormat = (apiTemplate) => {
    if (!apiTemplate) return defaultTemplate;

    console.log('Converting API template to component format:', apiTemplate);

    // If template already has selectedFields and customFields, use it as is
    if (apiTemplate.selectedFields && apiTemplate.customFields) {
      console.log('Template already in component format');
      return {
        ...defaultTemplate,
        ...apiTemplate,
        selectedFields: {
          ...defaultTemplate.selectedFields,
          ...apiTemplate.selectedFields
        },
        customFields: {
          ...defaultTemplate.customFields,
          ...apiTemplate.customFields
        },
        selectedDocuments: {
          ...defaultTemplate.selectedDocuments,
          ...(apiTemplate.selectedDocuments || {})
        },
        standardDocumentConfigs: apiTemplate.standardDocumentConfigs || {},
        customDocuments: apiTemplate.customDocuments || []
      };
    }

    // Convert from API sections format to component format
    const selectedFields = { ...defaultTemplate.selectedFields };
    const customFields = { ...defaultTemplate.customFields };
    const selectedDocuments = { ...defaultTemplate.selectedDocuments };
    const standardDocumentConfigs = {};
    const customDocuments = [];

    // Map sections to selectedFields and extract custom fields
    if (apiTemplate.sections) {
      apiTemplate.sections.forEach(section => {
        const sectionKey = getSectionKeyFromId(section.id);
        if (sectionKey) {
          selectedFields[sectionKey] = true;
          
          // Extract custom fields (fields that are not in the base field definitions)
          const baseFieldIds = getBaseFieldIds(sectionKey);
          const customFieldsInSection = section.fields?.filter(field => 
            !baseFieldIds.includes(field.id)
          ) || [];
          
          customFields[sectionKey] = customFieldsInSection.map(field => ({
            id: field.id,
            type: field.type,
            label: field.label,
            required: field.required,
            placeholder: field.placeholder || '',
            validation: field.validation || {},
            options: field.options || []
          }));
        }
      });
    }

    // Map document requirements to selectedDocuments and extract custom documents
    if (apiTemplate.documentRequirements) {
      apiTemplate.documentRequirements.forEach(doc => {
        const docKey = getDocumentKeyFromId(doc.id);
        if (docKey) {
          selectedDocuments[docKey] = true;
          // Store custom configuration for standard documents
          standardDocumentConfigs[docKey] = {
            description: doc.description,
            required: doc.required,
            acceptedFormats: doc.acceptedFormats || ['pdf', 'jpg', 'png']
          };
        } else {
          // This is a custom document
          customDocuments.push({
            id: doc.id,
            name: doc.name,
            description: doc.description || '',
            required: doc.required || false,
            acceptedFormats: doc.acceptedFormats || ['pdf', 'jpg', 'png'],
            category: doc.category || 'program-specific'
          });
        }
      });
    }

    const convertedTemplate = {
      ...defaultTemplate,
      ...apiTemplate,
      selectedFields,
      customFields,
      selectedDocuments,
      standardDocumentConfigs,
      customDocuments
    };

    console.log('Converted template:', convertedTemplate);
    return convertedTemplate;
  };

  // Helper function to map section IDs to component keys
  const getSectionKeyFromId = (sectionId) => {
    const mapping = {
      'personal_info': 'personalInfo',
      'emergency_contact': 'emergencyContact',
      'health_info': 'healthInfo',
      'parent_guardian': 'parentGuardian',
      'soccer_experience': 'soccerExperience',
      'medical_clearance': 'medicalClearance'
    };
    return mapping[sectionId];
  };

  // Helper function to get base field IDs for each section
  const getBaseFieldIds = (sectionKey) => {
    const baseFields = {
      personalInfo: ['firstName', 'lastName', 'email', 'dateOfBirth', 'phone', 'address', 'city', 'state', 'zipCode', 'country'],
      emergencyContact: ['emergencyContactName', 'emergencyContactPhone', 'emergencyContactRelationship', 'emergencyContactEmail'],
      healthInfo: ['allergies', 'medications', 'medicalConditions', 'dietaryRestrictions', 'emergencyMedicalInfo'],
      parentGuardian: ['parentName', 'parentEmail', 'parentPhone', 'parentRelationship'],
      soccerExperience: ['experienceLevel', 'previousClubs', 'preferredPosition', 'yearsPlaying'],
      medicalClearance: []
    };
    return baseFields[sectionKey] || [];
  };

  // Helper function to map document IDs to component keys
  const getDocumentKeyFromId = (docId) => {
    const mapping = {
      'birth_certificate': 'birthCertificate',
      'medical_clearance': 'medicalClearance',
      'photo_id': 'photoId',
      'insurance_info': 'insuranceInfo'
    };
    return mapping[docId];
  };

  // Initialize template data with proper defaults
  const initializeTemplateData = (template) => {
    return convertApiTemplateToComponentFormat(template);
  };

  const [templateData, setTemplateData] = useState(() => initializeTemplateData(template));

  // Update template data when template prop changes (for editing different templates)
  useEffect(() => {
    if (template) {
      setTemplateData(initializeTemplateData(template));
    }
  }, [template]);

  const categories = [
    { value: 'youth', label: 'Youth Programs', description: 'For players under 18' },
    { value: 'adult', label: 'Adult Programs', description: 'For adult players' },
    { value: 'competitive', label: 'Competitive Teams', description: 'Competitive leagues and tournaments' },
    { value: 'recreational', label: 'Recreational Programs', description: 'Casual and fun programs' },
    { value: 'camps', label: 'Soccer Camps', description: 'Training camps and sessions' },
    { value: 'clinics', label: 'Clinics', description: 'Skill-focused training clinics' },
    { value: 'tournaments', label: 'Tournaments', description: 'Tournament registrations' }
  ];

  const fieldOptions = [
    { 
      key: 'personalInfo', 
      label: 'Personal Information', 
      description: 'Name, email, phone, date of birth',
      required: true,
      icon: '👤'
    },
    { 
      key: 'emergencyContact', 
      label: 'Emergency Contact', 
      description: 'Emergency contact person details',
      required: true,
      icon: '🚨'
    },
    { 
      key: 'healthInfo', 
      label: 'Health Information', 
      description: 'Allergies, medications, medical conditions',
      icon: '🏥'
    },
    { 
      key: 'parentGuardian', 
      label: 'Parent/Guardian Info', 
      description: 'For youth programs - parent/guardian details',
      icon: '👨‍👩‍👧‍👦'
    },
    { 
      key: 'soccerExperience', 
      label: 'Soccer Experience', 
      description: 'Playing experience, position, skill level',
      icon: '⚽'
    },
    { 
      key: 'medicalClearance', 
      label: 'Medical Clearance', 
      description: 'Doctor clearance for participation',
      icon: '📋'
    }
  ];

  const fieldTypes = [
    { value: 'text', label: 'Text Input', description: 'Single line text' },
    { value: 'textarea', label: 'Text Area', description: 'Multi-line text' },
    { value: 'email', label: 'Email', description: 'Email address' },
    { value: 'tel', label: 'Phone', description: 'Phone number' },
    { value: 'date', label: 'Date', description: 'Date picker' },
    { value: 'number', label: 'Number', description: 'Numeric input' },
    { value: 'select', label: 'Dropdown', description: 'Select from options' },
    { value: 'radio', label: 'Radio Buttons', description: 'Single choice' },
    { value: 'checkbox', label: 'Checkboxes', description: 'Multiple choices' },
    { value: 'url', label: 'URL', description: 'Website address' }
  ];

  const documentOptions = [
    { 
      key: 'birthCertificate', 
      label: 'Birth Certificate', 
      description: 'For age verification (required for youth)',
      icon: '📄',
      category: 'identity'
    },
    { 
      key: 'medicalClearance', 
      label: 'Medical Clearance Form', 
      description: 'Doctor signed clearance form',
      icon: '🏥',
      category: 'medical'
    },
    { 
      key: 'photoId', 
      label: 'Photo ID', 
      description: 'Government issued photo identification',
      icon: '🆔',
      category: 'identity'
    },
    { 
      key: 'insuranceInfo', 
      label: 'Insurance Information', 
      description: 'Proof of medical insurance coverage',
      icon: '🛡️',
      category: 'medical'
    }
  ];

  const documentCategories = [
    { value: 'identity', label: 'Identity Documents', icon: '🆔' },
    { value: 'medical', label: 'Medical Documents', icon: '🏥' },
    { value: 'legal', label: 'Legal Documents', icon: '⚖️' },
    { value: 'program-specific', label: 'Program-Specific', icon: '⚽' }
  ];

  const fileFormatOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'jpg', label: 'JPG' },
    { value: 'jpeg', label: 'JPEG' },
    { value: 'png', label: 'PNG' },
    { value: 'doc', label: 'DOC' },
    { value: 'docx', label: 'DOCX' }
  ];

  const handleFieldChange = (field, value) => {
    setTemplateData(prev => ({
      ...prev,
      selectedFields: {
        ...prev.selectedFields,
        [field]: value
      }
    }));
  };

  const addCustomField = (sectionKey) => {
    const newField = {
      id: `custom_${Date.now()}`,
      type: 'text',
      label: '',
      required: false,
      placeholder: '',
      validation: {},
      options: []
    };
    
    setTemplateData(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [sectionKey]: [...(prev.customFields?.[sectionKey] || []), newField]
      }
    }));
  };

  const updateCustomField = (sectionKey, fieldIndex, updates) => {
    setTemplateData(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [sectionKey]: (prev.customFields?.[sectionKey] || []).map((field, index) => 
          index === fieldIndex ? { ...field, ...updates } : field
        )
      }
    }));
  };

  const removeCustomField = (sectionKey, fieldIndex) => {
    setTemplateData(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [sectionKey]: (prev.customFields?.[sectionKey] || []).filter((_, index) => index !== fieldIndex)
      }
    }));
  };

  const addFieldOption = (sectionKey, fieldIndex) => {
    const newOption = { value: '', label: '' };
    updateCustomField(sectionKey, fieldIndex, {
      options: [...(templateData.customFields?.[sectionKey]?.[fieldIndex]?.options || []), newOption]
    });
  };

  const updateFieldOption = (sectionKey, fieldIndex, optionIndex, updates) => {
    const field = templateData.customFields?.[sectionKey]?.[fieldIndex];
    const updatedOptions = field.options.map((option, index) => 
      index === optionIndex ? { ...option, ...updates } : option
    );
    updateCustomField(sectionKey, fieldIndex, { options: updatedOptions });
  };

  const removeFieldOption = (sectionKey, fieldIndex, optionIndex) => {
    const field = templateData.customFields?.[sectionKey]?.[fieldIndex];
    const updatedOptions = (field?.options || []).filter((_, index) => index !== optionIndex);
    updateCustomField(sectionKey, fieldIndex, { options: updatedOptions });
  };

  const validateCustomFields = () => {
    if (!templateData.customFields) return true;
    
    for (const sectionKey of Object.keys(templateData.customFields)) {
      const fields = templateData.customFields?.[sectionKey] || [];
      for (const field of fields) {
        // Check if field has a label
        if (!field?.label?.trim()) {
          return false;
        }
        // Check if select/radio/checkbox fields have options
        if (['select', 'radio', 'checkbox'].includes(field.type)) {
          if (!field.options || field.options.length === 0) {
            return false;
          }
          // Check if all options have values and labels
          for (const option of field.options) {
            if (!option?.value?.trim() || !option?.label?.trim()) {
              return false;
            }
          }
        }
      }
    }
    return true;
  };

  // Document management functions
  const addCustomDocument = () => {
    const newDocument = {
      id: `custom_doc_${Date.now()}`,
      name: '',
      description: '',
      required: false,
      acceptedFormats: ['pdf', 'jpg', 'png'],
      category: 'program-specific'
    };
    
    setTemplateData(prev => ({
      ...prev,
      customDocuments: [...(prev.customDocuments || []), newDocument]
    }));
  };

  const updateCustomDocument = (docIndex, updates) => {
    setTemplateData(prev => ({
      ...prev,
      customDocuments: (prev.customDocuments || []).map((doc, index) => 
        index === docIndex ? { ...doc, ...updates } : doc
      )
    }));
  };

  const removeCustomDocument = (docIndex) => {
    setTemplateData(prev => ({
      ...prev,
      customDocuments: (prev.customDocuments || []).filter((_, index) => index !== docIndex)
    }));
  };

  const validateCustomDocuments = () => {
    if (!templateData.customDocuments) return true;
    
    for (const doc of templateData.customDocuments) {
      if (!doc?.name?.trim()) {
        return false;
      }
    }
    return true;
  };

  // Standard document configuration functions
  const getStandardDocumentConfig = (docKey) => {
    return templateData.standardDocumentConfigs?.[docKey] || {};
  };

  const updateStandardDocumentConfig = (docKey, updates) => {
    setTemplateData(prev => ({
      ...prev,
      standardDocumentConfigs: {
        ...prev.standardDocumentConfigs,
        [docKey]: {
          ...prev.standardDocumentConfigs?.[docKey],
          ...updates
        }
      }
    }));
  };

  const getNextButtonDisabled = () => {
    switch (step) {
      case 1:
        // Step 1: Template basic information
        return !templateData.name.trim();
      case 2:
        // Step 2: Field selection - no validation needed, can proceed even with no fields selected
        return false;
      case 3:
        // Step 3: Document requirements - no validation needed, can proceed even with no documents selected
        return false;
      default:
        return false;
    }
  };

  const handleDocumentChange = (doc, value) => {
    setTemplateData(prev => ({
      ...prev,
      selectedDocuments: {
        ...prev.selectedDocuments,
        [doc]: value
      }
    }));
  };

  const generateTemplateStructure = () => {
    const sections = [];
    const documentRequirements = [];

    // Helper function to add custom fields to a section
    const addCustomFieldsToSection = (baseFields, sectionKey) => {
      const customFields = templateData.customFields[sectionKey] || [];
      const allFields = [
        ...baseFields,
        ...customFields.map(field => ({
          id: field.id,
          type: field.type,
          label: field.label,
          required: field.required,
          placeholder: field.placeholder,
          validation: field.validation,
          options: field.options
        }))
      ];
      
      // Deduplicate fields by ID to prevent duplicates
      const uniqueFields = [];
      const seenFieldIds = new Set();
      
      allFields.forEach(field => {
        if (!seenFieldIds.has(field.id)) {
          seenFieldIds.add(field.id);
          uniqueFields.push(field);
        }
      });
      
      console.log(`Section ${sectionKey}: ${allFields.length} -> ${uniqueFields.length} fields after deduplication`);
      return uniqueFields;
    };

    // Personal Information (always included)
    if (templateData.selectedFields.personalInfo) {
      const baseFields = [
          {
            id: "firstName",
            type: "text",
            label: "First Name",
            required: true,
            prefillFrom: "firstName",
            validation: { minLength: 2, maxLength: 50 }
          },
          {
            id: "lastName",
            type: "text",
            label: "Last Name",
            required: true,
            prefillFrom: "lastName",
            validation: { minLength: 2, maxLength: 50 }
          },
          {
            id: "email",
            type: "email",
            label: "Email Address",
            required: true,
            prefillFrom: "email",
            validation: { pattern: "email" }
          },
          {
            id: "dateOfBirth",
            type: "date",
            label: "Date of Birth",
            required: true,
            prefillFrom: "dateOfBirth"
          },
          {
            id: "phone",
            type: "tel",
            label: "Phone Number",
            required: false,
            prefillFrom: "phone"
          }
      ];

      sections.push({
        id: "personal_info",
        name: "Personal Information",
        description: "Basic personal details",
        icon: "user",
        order: 1,
        required: true,
        fields: addCustomFieldsToSection(baseFields, 'personalInfo')
      });
    }

    // Emergency Contact (always included)
    if (templateData.selectedFields.emergencyContact) {
      const baseFields = [
          {
            id: "emergencyName",
            type: "text",
            label: "Emergency Contact Name",
            required: true,
            validation: { minLength: 2, maxLength: 100 }
          },
          {
            id: "emergencyRelationship",
            type: "select",
            label: "Relationship",
            required: true,
            options: [
              { value: "parent", label: "Parent" },
              { value: "guardian", label: "Guardian" },
              { value: "spouse", label: "Spouse" },
              { value: "sibling", label: "Sibling" },
              { value: "friend", label: "Friend" },
              { value: "other", label: "Other" }
            ]
          },
          {
            id: "emergencyPhone",
            type: "tel",
            label: "Emergency Contact Phone",
            required: true,
            validation: { pattern: "phone" }
          }
      ];

      sections.push({
        id: "emergency_contact",
        name: "Emergency Contact",
        description: "Emergency contact information",
        icon: "phone",
        order: 2,
        required: true,
        fields: addCustomFieldsToSection(baseFields, 'emergencyContact')
      });
    }

    // Health Information
    if (templateData.selectedFields.healthInfo) {
      const baseFields = [
          {
            id: "allergies",
            type: "textarea",
            label: "Allergies",
            required: false,
            placeholder: "List any allergies or write 'None'"
          },
          {
            id: "medications",
            type: "textarea",
            label: "Current Medications",
            required: false,
            placeholder: "List current medications or write 'None'"
          },
          {
            id: "medicalConditions",
            type: "textarea",
            label: "Medical Conditions",
            required: false,
            placeholder: "Any medical conditions we should know about"
          }
      ];

      sections.push({
        id: "health_info",
        name: "Health Information",
        description: "Medical and health details",
        icon: "heart",
        order: 3,
        required: false,
        fields: addCustomFieldsToSection(baseFields, 'healthInfo')
      });
    }

    // Parent/Guardian Information
    if (templateData.selectedFields.parentGuardian) {
      const baseFields = [
          {
            id: "parentName",
            type: "text",
            label: "Parent/Guardian Name",
            required: true,
            validation: { minLength: 2, maxLength: 100 }
          },
          {
            id: "parentEmail",
            type: "email",
            label: "Parent/Guardian Email",
            required: true,
            validation: { pattern: "email" }
          },
          {
            id: "parentPhone",
            type: "tel",
            label: "Parent/Guardian Phone",
            required: true,
            validation: { pattern: "phone" }
          }
      ];

      sections.push({
        id: "parent_guardian",
        name: "Parent/Guardian Information",
        description: "Parent or guardian details (for youth programs)",
        icon: "users",
        order: 4,
        required: true,
        fields: addCustomFieldsToSection(baseFields, 'parentGuardian')
      });
    }

    // Soccer Experience
    if (templateData.selectedFields.soccerExperience) {
      const baseFields = [
          {
            id: "experienceLevel",
            type: "select",
            label: "Experience Level",
            required: true,
            options: [
              { value: "beginner", label: "Beginner" },
              { value: "intermediate", label: "Intermediate" },
              { value: "advanced", label: "Advanced" },
              { value: "professional", label: "Professional" }
            ]
          },
          {
            id: "preferredPosition",
            type: "select",
            label: "Preferred Position",
            required: false,
            options: [
              { value: "goalkeeper", label: "Goalkeeper" },
              { value: "defender", label: "Defender" },
              { value: "midfielder", label: "Midfielder" },
              { value: "forward", label: "Forward" },
              { value: "any", label: "Any Position" }
            ]
          },
          {
            id: "yearsPlaying",
            type: "number",
            label: "Years Playing Soccer",
            required: false,
            validation: { min: 0, max: 50 }
          }
      ];

      sections.push({
        id: "soccer_experience",
        name: "Soccer Experience",
        description: "Playing experience and preferences",
        icon: "trophy",
        order: 5,
        required: false,
        fields: addCustomFieldsToSection(baseFields, 'soccerExperience')
      });
    }

    // Document Requirements - Standard Documents with Custom Configuration
    const standardDocumentMapping = {
      birthCertificate: {
        id: "birth_certificate",
        name: "Birth Certificate",
        defaultDescription: "Official birth certificate for age verification",
        defaultRequired: templateData.category === 'youth',
        validityPeriod: "permanent"
      },
      medicalClearance: {
        id: "medical_clearance",
        name: "Medical Clearance Form",
        defaultDescription: "Doctor signed medical clearance for participation",
        defaultRequired: true,
        validityPeriod: "1_year"
      },
      photoId: {
        id: "photo_id",
        name: "Photo ID",
        defaultDescription: "Government-issued photo identification",
        defaultRequired: false,
        validityPeriod: "permanent"
      },
      insuranceInfo: {
        id: "insurance_info",
        name: "Insurance Information",
        defaultDescription: "Proof of medical insurance coverage",
        defaultRequired: false,
        validityPeriod: "1_year"
      }
    };

    Object.keys(standardDocumentMapping).forEach(docKey => {
      if (templateData.selectedDocuments[docKey]) {
        const docInfo = standardDocumentMapping[docKey];
        const config = templateData.standardDocumentConfigs?.[docKey] || {};
        
        documentRequirements.push({
          id: docInfo.id,
          name: docInfo.name,
          description: config.description || docInfo.defaultDescription,
          required: config.required !== undefined ? config.required : docInfo.defaultRequired,
          acceptedFormats: config.acceptedFormats || ["pdf", "jpg", "png"],
          maxSize: 5242880,
          validityPeriod: docInfo.validityPeriod
        });
      }
    });

    // Add custom documents
    if (templateData.customDocuments) {
      templateData.customDocuments.forEach(doc => {
        documentRequirements.push({
          id: doc.id,
          name: doc.name,
          description: doc.description,
          required: doc.required,
          acceptedFormats: doc.acceptedFormats,
          category: doc.category,
          maxSize: 5242880, // 5MB default
          validityPeriod: "permanent"
        });
      });
    }

    return {
      name: templateData.name,
      description: templateData.description,
      category: templateData.category,
      visibility: templateData.visibility,
      sections,
      documentRequirements,
      waivers: [
        {
          id: "liability_waiver",
          name: "Liability Waiver",
          required: true,
          content: "I understand that participation in soccer activities involves certain risks and I voluntarily assume all such risks. I release the club from any liability for injuries that may occur during participation.",
          version: "1.0"
        }
      ],
      settings: {
        autoSave: true,
        autoSaveInterval: 30000,
        allowPartialSubmission: true,
        sendConfirmationEmail: true,
        approvalRequired: true,
        paymentRequired: false
      }
    };
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const templateStructure = generateTemplateStructure();
      
      // Log the structure for debugging
      console.log('Template structure to save:', templateStructure);
      
      let result;
      if (isEditing && template) {
        // Get the template ID (could be _id or id depending on source)
        const templateId = template._id || template.id;
        
        if (!templateId) {
          throw new Error('Template ID is missing');
        }
        
        console.log('Updating template with ID:', templateId);
        
        // Update existing template
        result = await api.put(`/api/registration/templates/${templateId}`, templateStructure);
        alert('Template updated successfully!');
      } else {
        // Create new template
        result = await api.post('/api/registration/templates', templateStructure);
        alert('Template created successfully!');
      }
      
      if (onTemplateCreated) {
        onTemplateCreated(result.template || result);
      }
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} template:`, error);
      console.error('Error payload:', error.payload);
      
      // Show more detailed error if available
      let errorMessage = error.payload?.message || error.payload?.error || error.message;
      
      // If there are validation errors, format them nicely
      if (error.payload?.errors && Array.isArray(error.payload.errors)) {
        const validationErrors = error.payload.errors.map(e => `${e.path || e.param}: ${e.msg}`).join('\n');
        errorMessage = `Validation errors:\n${validationErrors}`;
      }
      
      alert(`Error ${isEditing ? 'updating' : 'creating'} template: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  };

  // Development utility function to clean existing template
  const handleCleanTemplate = async () => {
    if (!template || !template._id) {
      alert('No template to clean');
      return;
    }

    try {
      const analysis = analyzeTemplate(template);
      console.log('Template analysis:', analysis);

      if (!analysis.needsCleaning) {
        alert('Template is already clean - no duplicate fields found');
        return;
      }

      const result = await cleanTemplateInDatabase(template._id);
      console.log('Cleanup result:', result);

      if (result.success) {
        alert(`Template cleaned successfully! Removed ${analysis.totalDuplicates} duplicate fields.`);
        // Refresh the page or reload template data
        window.location.reload();
      } else {
        alert(`Failed to clean template: ${result.message}`);
      }
    } catch (error) {
      console.error('Error cleaning template:', error);
      alert(`Error cleaning template: ${error.message}`);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'youth': return '👶';
      case 'adult': return '👨';
      case 'competitive': return '🏆';
      case 'recreational': return '⚽';
      case 'camps': return '🏕️';
      case 'clinics': return '🎓';
      case 'tournaments': return '🎯';
      default: return '📋';
    }
  };

  const renderStep1 = () => (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>📋</div>
        <div className={styles.stepTitle}>
          <h3>Template Basic Information</h3>
          <p className={styles.stepDescription}>Set up the basic details for your registration templateData</p>
        </div>
      </div>

      <div className={styles.formSection}>
        <div className={styles.formRow}>
      <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelText}>Template Name</span>
              <span className={styles.required}>*</span>
            </label>
        <input
          type="text"
              value={templateData.name}
              onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., Youth Soccer Registration, Adult League Registration"
              className={`${styles.input} ${!templateData.name.trim() ? styles.inputError : ''}`}
        />
            {!templateData.name.trim() && (
              <span className={styles.errorText}>Template name is required</span>
            )}
          </div>
      </div>
      
        <div className={styles.formRow}>
      <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelText}>Description</span>
              <span className={styles.optional}>(Optional)</span>
            </label>
        <textarea
              value={templateData.description}
              onChange={(e) => setTemplateData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this templateData is for and when it should be used..."
          className={styles.textarea}
          rows={3}
        />
          </div>
        </div>
      </div>

      <div className={styles.formSection}>
        <div className={styles.sectionHeader}>
          <h4 className={styles.sectionTitle}>Category</h4>
          <span className={styles.required}>*</span>
          <p className={styles.sectionDescription}>Choose the category that best describes your registration templateData</p>
        </div>
        <div className={styles.categoryGrid}>
          {categories.map(cat => (
            <div 
              key={cat.value}
              className={`${styles.categoryCard} ${templateData.category === cat.value ? styles.selected : ''}`}
              onClick={() => setTemplateData(prev => ({ ...prev, category: cat.value }))}
            >
              <div className={styles.categoryIcon}>{getCategoryIcon(cat.value)}</div>
              <div className={styles.categoryContent}>
                <h4 className={styles.categoryTitle}>{cat.label}</h4>
                <p className={styles.categoryDescription}>{cat.description}</p>
              </div>
              {templateData.category === cat.value && (
                <div className={styles.categorySelected}>✓</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.formSection}>
        <div className={styles.sectionHeader}>
          <h4 className={styles.sectionTitle}>Visibility</h4>
          <p className={styles.sectionDescription}>Control who can access and use this templateData</p>
        </div>
        <div className={styles.visibilityOptions}>
          <div className={styles.visibilityOption}>
            <input
              type="radio"
              id="visibility-club"
              name="visibility"
              value="club"
              checked={templateData.visibility === 'club'}
              onChange={(e) => setTemplateData(prev => ({ ...prev, visibility: e.target.value }))}
              className={styles.radioInput}
            />
            <label htmlFor="visibility-club" className={styles.visibilityLabel}>
              <div className={styles.visibilityIcon}>🏢</div>
              <div className={styles.visibilityContent}>
                <span className={styles.visibilityTitle}>Club</span>
                <span className={styles.visibilityDescription}>Anyone in my club can use this templateData</span>
              </div>
            </label>
          </div>
          
          <div className={styles.visibilityOption}>
            <input
              type="radio"
              id="visibility-private"
              name="visibility"
              value="private"
              checked={templateData.visibility === 'private'}
              onChange={(e) => setTemplateData(prev => ({ ...prev, visibility: e.target.value }))}
              className={styles.radioInput}
            />
            <label htmlFor="visibility-private" className={styles.visibilityLabel}>
              <div className={styles.visibilityIcon}>🔒</div>
              <div className={styles.visibilityContent}>
                <span className={styles.visibilityTitle}>Private</span>
                <span className={styles.visibilityDescription}>Only I can use this templateData</span>
              </div>
            </label>
          </div>
          
          <div className={styles.visibilityOption}>
            <input
              type="radio"
              id="visibility-public"
              name="visibility"
              value="public"
              checked={templateData.visibility === 'public'}
              onChange={(e) => setTemplateData(prev => ({ ...prev, visibility: e.target.value }))}
              className={styles.radioInput}
            />
            <label htmlFor="visibility-public" className={styles.visibilityLabel}>
              <div className={styles.visibilityIcon}>🌐</div>
              <div className={styles.visibilityContent}>
                <span className={styles.visibilityTitle}>Public</span>
                <span className={styles.visibilityDescription}>Anyone can use this templateData</span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>📝</div>
        <div className={styles.stepTitle}>
          <h3>Select Registration Fields</h3>
          <p className={styles.stepDescription}>Choose what information you want to collect from participants</p>
        </div>
      </div>
      
      <div className={styles.formSection}>
      <div className={styles.fieldsGrid}>
        {fieldOptions.map(field => (
            <div key={field.key} className={`${styles.fieldCard} ${templateData.selectedFields?.[field.key] ? styles.fieldSelected : ''}`}>
            <div className={styles.fieldHeader}>
                <label className={styles.fieldCheckbox}>
                <input
                  type="checkbox"
                    checked={templateData.selectedFields?.[field.key] || false}
                  onChange={(e) => handleFieldChange(field.key, e.target.checked)}
                  disabled={field.required}
                    className={styles.checkboxInput}
                />
                <span className={styles.checkmark}></span>
                  <div className={styles.fieldContent}>
                    <div className={styles.fieldTitleRow}>
                      <span className={styles.fieldIcon}>{field.icon}</span>
                      <strong className={styles.fieldTitle}>{field.label}</strong>
                      {field.required && <span className={styles.requiredBadge}>Required</span>}
                    </div>
                  </div>
              </label>
            </div>
            <p className={styles.fieldDescription}>{field.description}</p>
              
              {/* Custom Fields Management */}
              {templateData.selectedFields?.[field.key] && (
                <div className={styles.customFieldsSection}>
                  <div className={styles.customFieldsHeader}>
                    <span className={styles.customFieldsTitle}>Custom Fields</span>
                    <button
                      type="button"
                      onClick={() => addCustomField(field.key)}
                      className={styles.addFieldButton}
                    >
                      + Add Field
                    </button>
          </div>
                  
                  {templateData.customFields?.[field.key]?.map((customField, index) => (
                    <div key={customField.id} className={styles.customFieldEditor}>
                      <div className={styles.customFieldRow}>
                        <input
                          type="text"
                          placeholder="Field Label"
                          value={customField.label}
                          onChange={(e) => updateCustomField(field.key, index, { label: e.target.value })}
                          className={styles.customFieldInput}
                        />
                        <select
                          value={customField.type}
                          onChange={(e) => updateCustomField(field.key, index, { type: e.target.value })}
                          className={styles.customFieldSelect}
                        >
                          {fieldTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                        <label className={styles.customFieldCheckbox}>
                          <input
                            type="checkbox"
                            checked={customField.required}
                            onChange={(e) => updateCustomField(field.key, index, { required: e.target.checked })}
                          />
                          Required
                        </label>
                        <button
                          type="button"
                          onClick={() => removeCustomField(field.key, index)}
                          className={styles.removeFieldButton}
                        >
                          🗑️
                        </button>
                      </div>
                      
                      {/* Options for select/radio/checkbox fields */}
                      {['select', 'radio', 'checkbox'].includes(customField.type) && (
                        <div className={styles.fieldOptionsSection}>
                          <div className={styles.fieldOptionsHeader}>
                            <span>Options:</span>
                            <button
                              type="button"
                              onClick={() => addFieldOption(field.key, index)}
                              className={styles.addOptionButton}
                            >
                              + Add Option
                            </button>
                          </div>
                          {customField.options?.map((option, optionIndex) => (
                            <div key={optionIndex} className={styles.fieldOptionRow}>
                              <input
                                type="text"
                                placeholder="Value"
                                value={option.value}
                                onChange={(e) => updateFieldOption(field.key, index, optionIndex, { value: e.target.value })}
                                className={styles.optionInput}
                              />
                              <input
                                type="text"
                                placeholder="Label"
                                value={option.label}
                                onChange={(e) => updateFieldOption(field.key, index, optionIndex, { label: e.target.value })}
                                className={styles.optionInput}
                              />
                              <button
                                type="button"
                                onClick={() => removeFieldOption(field.key, index, optionIndex)}
                                className={styles.removeOptionButton}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>📄</div>
        <div className={styles.stepTitle}>
          <h3>Document Requirements</h3>
          <p className={styles.stepDescription}>Select which documents participants need to upload</p>
        </div>
      </div>
      
      <div className={styles.formSection}>
        {/* Standard Documents */}
        <div className={styles.sectionHeader}>
          <h4 className={styles.sectionTitle}>Standard Documents</h4>
          <p className={styles.sectionDescription}>Common documents used in registration</p>
        </div>
        
        <div className={styles.fieldsGrid}>
          {documentOptions.map(doc => (
            <div key={doc.key} className={`${styles.fieldCard} ${templateData.selectedDocuments?.[doc.key] ? styles.fieldSelected : ''}`}>
              <div className={styles.fieldHeader}>
                <label className={styles.fieldCheckbox}>
                  <input
                    type="checkbox"
                    checked={templateData.selectedDocuments?.[doc.key] || false}
                    onChange={(e) => handleDocumentChange(doc.key, e.target.checked)}
                    className={styles.checkboxInput}
                  />
                  <span className={styles.checkmark}></span>
                  <div className={styles.fieldContent}>
                    <div className={styles.fieldTitleRow}>
                      <span className={styles.fieldIcon}>{doc.icon}</span>
                      <strong className={styles.fieldTitle}>{doc.label}</strong>
                      <span className={styles.categoryBadge}>{documentCategories.find(c => c.value === doc.category)?.label}</span>
                    </div>
                  </div>
                </label>
              </div>
              <p className={styles.fieldDescription}>{doc.description}</p>
              
              {/* Standard Document Configuration */}
              {templateData.selectedDocuments?.[doc.key] && (
                <div className={styles.standardDocumentConfig}>
                  <div className={styles.configHeader}>
                    <span className={styles.configTitle}>Document Settings</span>
                  </div>
                  
                  <div className={styles.configRow}>
                    <label className={styles.configLabel}>Description:</label>
                    <input
                      type="text"
                      placeholder="Document description"
                      value={getStandardDocumentConfig(doc.key)?.description || doc.description}
                      onChange={(e) => updateStandardDocumentConfig(doc.key, { description: e.target.value })}
                      className={styles.configInput}
                    />
                  </div>
                  
                  <div className={styles.configRow}>
                    <label className={styles.configCheckbox}>
                      <input
                        type="checkbox"
                        checked={getStandardDocumentConfig(doc.key)?.required || false}
                        onChange={(e) => updateStandardDocumentConfig(doc.key, { required: e.target.checked })}
                      />
                      Required Document
                    </label>
                  </div>
                  
                  <div className={styles.fileFormatsSection}>
                    <label className={styles.fileFormatsLabel}>Accepted Formats:</label>
                    <div className={styles.fileFormatsGrid}>
                      {fileFormatOptions.map(format => {
                        const currentFormats = getStandardDocumentConfig(doc.key)?.acceptedFormats || ['pdf', 'jpg', 'png'];
                        return (
                          <label key={format.value} className={styles.formatCheckbox}>
                            <input
                              type="checkbox"
                              checked={currentFormats.includes(format.value)}
                              onChange={(e) => {
                                const formats = e.target.checked
                                  ? [...currentFormats, format.value]
                                  : currentFormats.filter(f => f !== format.value);
                                updateStandardDocumentConfig(doc.key, { acceptedFormats: formats });
                              }}
                            />
                            {format.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Custom Documents */}
        <div className={styles.customDocumentsSection}>
          <div className={styles.customDocumentsHeader}>
            <h4 className={styles.sectionTitle}>Custom Documents</h4>
            <button
              type="button"
              onClick={addCustomDocument}
              className={styles.addFieldButton}
            >
              + Add Document
            </button>
          </div>
          
          {templateData.customDocuments && templateData.customDocuments.length > 0 && (
            <div className={styles.customDocumentsList}>
              {templateData.customDocuments.map((doc, index) => (
                <div key={doc.id} className={styles.customDocumentEditor}>
                  <div className={styles.customDocumentRow}>
                    <input
                      type="text"
                      placeholder="Document Name"
                      value={doc.name}
                      onChange={(e) => updateCustomDocument(index, { name: e.target.value })}
                      className={styles.customFieldInput}
                    />
                    
                    <select
                      value={doc.category}
                      onChange={(e) => updateCustomDocument(index, { category: e.target.value })}
                      className={styles.customFieldSelect}
                    >
                      {documentCategories.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.icon} {category.label}
                        </option>
                      ))}
                    </select>
                    
                    <label className={styles.customFieldCheckbox}>
                      <input
                        type="checkbox"
                        checked={doc.required}
                        onChange={(e) => updateCustomDocument(index, { required: e.target.checked })}
                      />
                      Required
                    </label>
                    
                    <button
                      type="button"
                      onClick={() => removeCustomDocument(index)}
                      className={styles.removeFieldButton}
                      title="Remove document"
                    >
                      🗑️
                    </button>
                  </div>
                  
                  <div className={styles.customDocumentRow}>
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={doc.description}
                      onChange={(e) => updateCustomDocument(index, { description: e.target.value })}
                      className={styles.customFieldInput}
                      style={{ flex: 2 }}
                    />
                  </div>
                  
                  <div className={styles.fileFormatsSection}>
                    <label className={styles.fileFormatsLabel}>Accepted Formats:</label>
                    <div className={styles.fileFormatsGrid}>
                      {fileFormatOptions.map(format => (
                        <label key={format.value} className={styles.formatCheckbox}>
                          <input
                            type="checkbox"
                            checked={doc.acceptedFormats.includes(format.value)}
                            onChange={(e) => {
                              const formats = e.target.checked
                                ? [...doc.acceptedFormats, format.value]
                                : doc.acceptedFormats.filter(f => f !== format.value);
                              updateCustomDocument(index, { acceptedFormats: formats });
                            }}
                          />
                          {format.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {(!templateData.customDocuments || templateData.customDocuments.length === 0) && (
            <div className={styles.noCustomDocuments}>
              <p>No custom documents added yet. Click "Add Document" to create custom document requirements.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>✅</div>
        <div className={styles.stepTitle}>
          <h3>Review & Create</h3>
          <p className={styles.stepDescription}>Review your templateData configuration and create it</p>
        </div>
        </div>

      <div className={styles.summaryContainer}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <h4 className={styles.summaryTitle}>📋 Template Details</h4>
          </div>
          <div className={styles.summaryContent}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Name:</span>
              <span className={styles.summaryValue}>{templateData.name || 'Untitled Template'}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Category:</span>
              <span className={styles.summaryValue}>{categories.find(c => c.value === templateData.category)?.label}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Visibility:</span>
              <span className={styles.summaryValue}>{templateData.visibility.charAt(0).toUpperCase() + templateData.visibility.slice(1)}</span>
            </div>
            {templateData.description && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Description:</span>
                <span className={styles.summaryValue}>{templateData.description}</span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <h4 className={styles.summaryTitle}>📝 Registration Fields</h4>
            <span className={styles.summaryCount}>
              {Object.values(templateData.selectedFields).filter(Boolean).length} sections, 
              {Object.values(templateData.customFields).flat().length} custom fields
            </span>
          </div>
          <div className={styles.summaryContent}>
            {fieldOptions.filter(f => templateData.selectedFields[f.key]).length > 0 ? (
              <div className={styles.summaryList}>
                {fieldOptions.filter(f => templateData.selectedFields[f.key]).map(f => (
                  <div key={f.key} className={styles.summarySection}>
                    <div className={styles.summaryListItem}>
                      <span className={styles.summaryItemIcon}>✓</span>
                      <span className={styles.summaryItemText}>
                        {f.icon} {f.label}
                      </span>
                      {f.required && <span className={styles.summaryItemBadge}>Required</span>}
                    </div>
                    {templateData.customFields[f.key]?.length > 0 && (
                      <div className={styles.customFieldsSummary}>
                        {templateData.customFields[f.key].map((field, index) => (
                          <div key={field.id} className={styles.customFieldSummaryItem}>
                            <span className={styles.customFieldIcon}>+</span>
                            <span className={styles.customFieldName}>
                              {field.label || 'Untitled Field'} ({field.type})
                            </span>
                            {field.required && <span className={styles.customFieldRequired}>Required</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.summaryEmpty}>No fields selected</p>
            )}
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <h4 className={styles.summaryTitle}>📄 Document Requirements</h4>
            <span className={styles.summaryCount}>
              {Object.values(templateData.selectedDocuments).filter(Boolean).length + (templateData.customDocuments?.length || 0)} total
            </span>
          </div>
          <div className={styles.summaryContent}>
            {(documentOptions.filter(d => templateData.selectedDocuments?.[d.key]).length > 0 || (templateData.customDocuments?.length || 0) > 0) ? (
              <div className={styles.summaryList}>
                {/* Standard Documents */}
                {documentOptions.filter(d => templateData.selectedDocuments?.[d.key]).map(d => {
                  const config = getStandardDocumentConfig(d.key);
                  return (
                    <div key={d.key} className={styles.summaryListItem}>
                      <span className={styles.summaryItemIcon}>✓</span>
                      <span className={styles.summaryItemText}>{d.label}</span>
                      <span className={styles.summaryItemBadge}>Standard</span>
                      {config.required && <span className={styles.requiredBadge}>Required</span>}
                    </div>
                  );
                })}
                
                {/* Custom Documents */}
                {templateData.customDocuments?.map(doc => (
                  <div key={doc.id} className={styles.summaryListItem}>
                    <span className={styles.summaryItemIcon}>✓</span>
                    <span className={styles.summaryItemText}>{doc.name}</span>
                    <span className={styles.summaryItemBadge}>
                      {documentCategories.find(c => c.value === doc.category)?.label || 'Custom'}
                    </span>
                    {doc.required && <span className={styles.requiredBadge}>Required</span>}
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.summaryEmpty}>No documents required</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Handle overlay click to close modal (click outside modal content)
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return createPortal(
    <div className={styles.wizardOverlay} onClick={handleOverlayClick}>
      <div className={styles.wizard} onClick={(e) => e.stopPropagation()}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
           <h2>{isEditing ? 'Edit Registration Template' : 'Create Registration Template'}</h2>
        <div className={styles.steps}>
          {[1, 2, 3, 4].map(s => (
            <div key={s} className={`${styles.stepIndicator} ${step >= s ? styles.active : ''}`}>
              {s}
            </div>
          ))}
          </div>
        </div>
        <div className={styles.headerControls}>
          <button 
            className={styles.closeButton}
            onClick={onCancel}
            title="Close"
          >
            ✕
          </button>
        </div>
      </div>

      <div className={styles.content}>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </div>

      <div className={styles.actions}>
        <button 
          onClick={onCancel}
          className={styles.cancelButton}
        >
          Cancel
        </button>
        
        {step > 1 && (
          <button 
            onClick={() => setStep(step - 1)}
            className={styles.backButton}
          >
            Back
          </button>
        )}
        
        {step < 4 ? (
          <button 
            onClick={() => setStep(step + 1)}
            disabled={getNextButtonDisabled()}
            className={styles.nextButton}
          >
            Next
          </button>
        ) : (
          <button 
            onClick={handleCreate}
            disabled={creating || !templateData.name.trim() || !validateCustomFields() || !validateCustomDocuments()}
            className={styles.createButton}
          >
            {creating ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Template' : 'Create Template')}
          </button>
        )}
      </div>
      </div>
    </div>,
    document.body
  );
};

export default TemplateCreationWizard;
